import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getSettingsClass } from '../../utils/helpers';
import StarRating from '../fragments/star-rating';
import { graphql } from 'gatsby';

interface Settings {
  css_class_name: string;
  custom_css: string;
  margin_bottom: string;
  margin_top: string;
  padding_bottom: string;
  padding_top: string;
}

interface ReviewItemProps {
  author_name?: string;
  author_title?: string;
  cta_text?: string;
  cta_url?: string;
  date?: string;
  image: string;
  image_field: ImageField<ReusableImageField>;
  logo: string;
  name: string;
  stars: string;
  tags?: string[];
  text: string;
  title: string;
}

interface ReviewListProps {
  reviews: ReviewItemProps[];
  rows_no: string;
  settings: Settings;
}

const ReviewList: React.FC<ReviewListProps> = ({
  reviews: inputReviews,
  rows_no,
  settings,
}) => {
  const settingsClass = getSettingsClass(settings);
  const [reviews, setReviews] = useState<ReviewItemProps[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(event.clientX);
    setStartScrollLeft(scrollRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const diffX = event.clientX - startX;
    scrollRef.current.scrollLeft = startScrollLeft - diffX;
  };

  useEffect(() => {
    if (inputReviews) {
      setReviews(inputReviews);
    } else {
      (async () => {
        const jsonData = (await import('~/data/sidebar/reviews.json'))
          .default;
        //@ts-ignore - I don't know why JSON types are not mathing
        setReviews(jsonData.reviews);
      })();
    }
  }, [inputReviews]);

  if (!reviews?.length) return null;

  return (
    <div className={`block ${settingsClass}`}>
      <div className='block__review_list'>
        <div
          ref={scrollRef}
          className='review_list'
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          <div className='review-padding-item' />
          {reviews.map(review => (
            <ReviewItem
              key={review.title}
              review={review}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ReviewItem: React.FC<{ review: ReviewItemProps }> = ({ review }) => {
  if (!review) return null;

  let imageStyle = {} as React.CSSProperties;
  let reviewImageSrc = review.image;
  
  if (!review.image && review.image_field) {
    imageStyle = {
      '--translate-x': `${review.image_field.offset?.horizontal ?? 0}rem`,
      '--translate-y': `${review.image_field.offset?.vertical ?? 0}rem`,
      '--scale-x': review.image_field.scale?.x ?? 1,
      '--scale-y': review.image_field.scale?.y ?? 1,
    } as React.CSSProperties
    reviewImageSrc = review.image_field.src;
  }

  return (
    <div className='review-item scale-on-hover'>
      <div className='review-content'>
        {review.date && <h5 className='review-item__date'>{review.date}</h5>}
        <h3 className='review-item__title'>{review.title}</h3>
        <StarRating
          rating={parseInt(review.stars)}
          maxRating={5}
          numberOfStars={5}
        />

        <p
          className='review-item__text'
          dangerouslySetInnerHTML={{ __html: review.text }}
        />
      </div>
      {review.tags && (
        <div className='review-item__tags'>
          {review.tags.map(tag => (
            <span
              key={tag}
              className='review-item__tag'
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      <div className='review-author'>
        <div className='review-author__content'>
          <h4 className='review-author__name'>{review.author_name}</h4>
          <p className='review-author__title'>{review.author_title}</p>
          <img
            className='review-author__logo'
            src={review.logo}
            alt={`${review.author_name}'s company logo`}
            loading='lazy'
          />
        </div>
        <img
          className='review-author__image'
          src={reviewImageSrc}
          alt={review.author_name}
          style={imageStyle}
          loading='lazy'
        />
      </div>
    </div>
  );
};

export const reviewFragment = graphql`
  fragment ReviewFragment on Review {
    author_name
    author_title
    cta_text
    cta_url
    date
    image
    image_field {
      ...ImageFieldFragment
    }
    name
    stars
    text
    title
  }
`

export default ReviewList;
